import {
  Menu as AntMenu,
  Badge,
  Button,
  Drawer,
  Dropdown,
  MenuProps,
  theme,
} from "antd";
import {
  AppstoreOutlined,
  BellOutlined,
  DollarOutlined,
  DownOutlined,
  GlobalOutlined,
  LoadingOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuOutlined,
  MenuUnfoldOutlined,
  QrcodeOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { BsChatDots } from "react-icons/bs";
import { Lang } from "@axl-dev/school-sdk";
import {
  ReactNode,
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { buildCabinetMenuItems } from "@app/shared/lib/cabinet/buildMenuItems";
import { CabinetPage } from "@app/shared/lib/cabinet/types";
import { useCabinet } from "@app/shared/lib/cabinet/CabinetProvider";
import { useDevice } from "@app/shared/lib/DeviceProvider";
import {
  useAuth,
  useDrawer,
  useLang,
  useLoc,
  useRouter,
  useSchool,
  useSdk,
} from "@app/shared/lib/hooks";
import { useUser } from "@app/shared/lib/hooks/useUser";
import { DynamicIcon } from "@app/shared/ui/DynamicIcon";
import { GamificationBalance } from "@app/shared/ui/GamificationBalance";
import { Loc } from "@app/shared/ui/Loc";
import { LoadingOverlay } from "@app/shared/ui/LoadingOverlay";
import { Price } from "@app/shared/ui/Price";
import { SchoolLogo } from "@app/shared/ui/SchoolLogo/SchoolLogo";
import { useUIAppState } from "@app/shared/ui/UIAppState/UIAppStateProvider";
import { UserAvatar } from "@app/shared/ui/UserAvatar";
import { sanitizeCssValue as sc } from "@app/widgets/shared/btnHoverCss";
import { attr, bool } from "@app/widgets/shared/parseAttr";

const UserNotifications = lazy(
  () =>
    import(
      "@app/shared/ui/BasicLayout/components/Header/Notifications/components/UserNotifications/UserNotifications"
    ),
);

export type CabinetMenuWidgetProps = {
  showLogo: boolean;
  logoHeight: number;
  orientation: "horizontal" | "vertical";
  bgColor: string;
  textColor: string;
  activeColor: string;
  showActiveBar: boolean;
  mobileMenuPlacement: "left" | "right";
  mobileMenuMask: boolean;
  burgerIconSize: string;
  burgerIconColor: string;
  burgerWidth: string;
  fontFamily: string;
  fontSize: string;
  sticky: boolean;
  showChat: boolean;
  showNotifications: boolean;
  showGamification: boolean;
  showAccount: boolean;
  itemBgColor: string;
  itemBgActiveColor: string;
  itemPaddingV: string;
  itemPaddingH: string;
  itemBorderRadius: string;
  itemShadow: string;
  itemGap: string;
  actionIconColor: string;
  actionBgColor: string;
  actionIconSize: string;
  actionBorderRadius: string;
  accountNameColor: string;
  accountNameSize: string;
  accountAvatarSize: string;
  accountShowName: boolean;
  dropdownBgColor: string;
  dropdownTextColor: string;
  dropdownIconColor: string;
  dropdownFontSize: string;
  gamBgColor: string;
  gamTextColor: string;
  gamFontSize: string;
  gamPaddingV: string;
  gamPaddingH: string;
  gamBorderRadius: string;
  profileBgColor: string;
  profileFontFamily: string;
  profileNameColor: string;
  profileNameFontSize: string;
  profileEmailColor: string;
  profileEmailFontSize: string;
  profileBtnBgColor: string;
  profileBtnTextColor: string;
  profileBtnBorderRadius: string;
};

type MenuItem = {
  key: string;
  label: ReactNode;
  onClick?: () => void;
  children?: MenuItem[];
};

export const CabinetMenuWidget: React.FC<CabinetMenuWidgetProps> = (props) => {
  const { navigate } = useRouter();
  const loc = useLoc();
  const school = useSchool();
  const sdk = useSdk();
  const { user } = useUser();
  const { logout } = useAuth();
  const { lang, selectLang } = useLang();
  const device = useDevice();
  const { rootPages, childrenOf } = useCabinet();
  const { token } = theme.useToken();
  const menuBg = props.bgColor || "#ffffff";
  const menuText = props.textColor || "rgba(0, 0, 0, 0.88)";
  const menuActive = props.activeColor || token.colorPrimary;
  const menuActionBg = props.actionBgColor || token.colorFillTertiary;
  const menuActionIcon = props.actionIconColor || token.colorTextSecondary;
  const menuDropdownBg = props.dropdownBgColor || token.colorBgElevated;
  const menuDropdownText = props.dropdownTextColor || token.colorText;
  const menuDropdownIcon = props.dropdownIconColor || token.colorTextSecondary;
  const { setIsProfileDrawerVisible, setCabinetProfileSettings } =
    useUIAppState();
  const notificationDrawer = useDrawer();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const isMobile = device.deviceSize !== "desktop";
  const isVertical = props.orientation === "vertical";

  useEffect(() => {
    setCabinetProfileSettings({
      bgColor: props.profileBgColor,
      fontFamily: props.profileFontFamily,
      nameColor: props.profileNameColor,
      nameFontSize: props.profileNameFontSize,
      emailColor: props.profileEmailColor,
      emailFontSize: props.profileEmailFontSize,
      btnBgColor: props.profileBtnBgColor,
      btnTextColor: props.profileBtnTextColor,
      btnBorderRadius: props.profileBtnBorderRadius,
    });
    return () => setCabinetProfileSettings(null);
  }, [
    setCabinetProfileSettings,
    props.profileBgColor,
    props.profileFontFamily,
    props.profileNameColor,
    props.profileNameFontSize,
    props.profileEmailColor,
    props.profileEmailFontSize,
    props.profileBtnBgColor,
    props.profileBtnTextColor,
    props.profileBtnBorderRadius,
  ]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result = await sdk.api.notification.fetchCount({ read: false });
      if (!cancelled && result.success) setUnreadCount(result.body.count);
    })();
    const unsub = sdk.hub.user.notification.count.subscribe((x: number) =>
      setUnreadCount(x),
    );
    return () => {
      cancelled = true;
      unsub();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pageIcon = useCallback((page: CabinetPage, size = 16): ReactNode => {
    const inner = page.icon
      ? (/^(Ai|Fc|Ri)/.test(page.icon)
          ? <DynamicIcon name={page.icon} size={size} />
          : <span style={{ fontSize: size, lineHeight: 1 }}>{page.icon}</span>)
      : <AppstoreOutlined style={{ fontSize: size }} />;
    return (
      <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: size, height: size, lineHeight: 1, flexShrink: 0 }}>
        {inner}
      </span>
    );
  }, []);

  const menuItems = useMemo<MenuItem[]>(() => {
    if (rootPages.length === 0) {
      const go = (path: string) => () => {
        navigate(path);
        setMobileOpen(false);
      };
      return [
        {
          key: "/",
          icon: <span style={{ fontWeight: 600 }}>К</span>,
          label: loc.word("Courses and materials"),
          onClick: go("/"),
        },
        {
          key: "/access",
          icon: <span style={{ fontWeight: 600 }}>Д</span>,
          label: loc.word("Access and payments"),
          onClick: go("/access"),
        },
      ];
    }

    return buildCabinetMenuItems<MenuItem>(
      rootPages,
      childrenOf,
      (page, path) => ({
        key: path,
        icon: pageIcon(page),
        label: page.name,
        onClick: () => {
          navigate(path);
          setMobileOpen(false);
        },
      }),
      (page, path, children) => {
        const icon = pageIcon(page);
        const selfItem: MenuItem = {
          key: path,
          icon,
          label: page.name,
          onClick: () => {
            navigate(path);
            setMobileOpen(false);
          },
        };
        return {
          key: `${path}__group`,
          icon,
          label: page.name,
          children: [selfItem, ...children],
        };
      },
    );
  }, [rootPages, childrenOf, pageIcon, navigate, loc]);

  const scopedCss = `
    .ez-cabinet-menu-widget {
      ${props.fontFamily ? `font-family: ${sc(props.fontFamily)} !important;` : ""}
    }
    .ez-cabinet-menu-widget .ant-menu {
      background: transparent !important;
      border: none !important;
      color: ${sc(menuText)} !important;
      font-size: ${sc(props.fontSize)} !important;
      ${props.fontFamily ? `font-family: ${sc(props.fontFamily)} !important;` : ""}
    }
    .ez-cabinet-menu-widget .ant-menu-horizontal {
      gap: ${sc(props.itemGap || "4px")};
      display: flex !important;
    }
    .ez-cabinet-menu-widget .ant-menu-inline .ant-menu-item,
    .ez-cabinet-menu-widget .ant-menu-inline .ant-menu-submenu {
      margin-bottom: ${sc(props.itemGap || "4px")} !important;
    }
    .ez-cabinet-menu-widget .ant-menu-item,
    .ez-cabinet-menu-widget .ant-menu-submenu-title {
      color: ${sc(menuText)} !important;
      gap: 8px;
      margin: 0 !important;
      margin-inline: 0 !important;
    }
    .ez-cabinet-menu-widget .ant-menu-submenu {
      margin: 0 !important;
      margin-inline: 0 !important;
      padding: 0 !important;
    }
    .ez-cabinet-menu-widget .ant-menu-overflow-item {
      margin: 0 !important;
      margin-inline: 0 !important;
      padding-inline: 0 !important;
    }
    .ez-cabinet-menu-widget li.ant-menu-item-selected,
    .ez-cabinet-menu-widget li.ant-menu-item-active,
    .ez-cabinet-menu-widget .ant-menu-item-selected,
    .ez-cabinet-menu-widget .ant-menu-item-active,
    .ez-cabinet-menu-widget .ant-menu-item-selected .ant-menu-title-content,
    .ez-cabinet-menu-widget .ant-menu-item-active .ant-menu-title-content {
      color: ${sc(menuActive)} !important;
    }
    .ez-cabinet-menu-widget .ant-menu-item-selected .anticon,
    .ez-cabinet-menu-widget .ant-menu-item-active .anticon {
      color: ${sc(menuActive)} !important;
    }
    .ez-cabinet-menu-widget .ant-menu-item-selected::after,
    .ez-cabinet-menu-widget .ant-menu-item-active::after {
      border-color: ${sc(menuActive)} !important;
    }
    .ez-cabinet-menu-widget .ant-menu-submenu-selected > .ant-menu-submenu-title {
      color: ${sc(menuActive)} !important;
    }
    .ez-cabinet-menu-widget .ant-menu-item,
    .ez-cabinet-menu-widget .ant-menu-submenu-title {
      padding: ${sc(props.itemPaddingV || "8px")} ${sc(props.itemPaddingH || "16px")} !important;
      border-radius: ${sc(props.itemBorderRadius || "0px")} !important;
      ${props.itemBgColor ? `background-color: ${sc(props.itemBgColor)} !important;` : ""}
      ${props.itemShadow ? `box-shadow: ${sc(props.itemShadow)} !important;` : ""}
    }
    .ez-cabinet-menu-widget .ant-menu-horizontal > .ant-menu-item,
    .ez-cabinet-menu-widget .ant-menu-horizontal > .ant-menu-submenu,
    .ez-cabinet-menu-widget .ant-menu-horizontal > .ant-menu-submenu > .ant-menu-submenu-title {
      height: auto !important;
      line-height: 1.4 !important;
      top: 0 !important;
    }
    ${!props.showActiveBar ? `
    .ez-cabinet-menu-widget .ant-menu-item::after,
    .ez-cabinet-menu-widget .ant-menu-submenu::after,
    .ez-cabinet-menu-widget .ant-menu-horizontal > .ant-menu-item::after,
    .ez-cabinet-menu-widget .ant-menu-horizontal > .ant-menu-submenu::after {
      display: none !important;
      border-bottom: none !important;
    }` : ""}
    .ez-cabinet-menu-widget .ant-menu-item-selected {
      ${props.itemBgActiveColor ? `background-color: ${sc(props.itemBgActiveColor)} !important;` : ""}
    }
    .ez-cabinet-menu-widget .ant-menu-horizontal .ant-menu-item,
    .ez-cabinet-menu-widget .ant-menu-horizontal .ant-menu-submenu-title {
      display: inline-flex !important;
      align-items: center !important;
    }
    .ez-cabinet-menu-widget .ant-menu-item svg,
    .ez-cabinet-menu-widget .ant-menu-submenu-title svg {
      vertical-align: middle;
    }
    .ez-cabinet-menu-widget .ez-action-btn {
      width: ${parseInt(props.actionIconSize || "18") + 14}px;
      height: ${parseInt(props.actionIconSize || "18") + 14}px;
      border-radius: ${sc(props.actionBorderRadius || "50%")} !important;
      background-color: ${sc(menuActionBg)} !important;
      color: ${sc(menuActionIcon)} !important;
      font-size: ${sc(props.actionIconSize || "18px")} !important;
      display: flex !important;
      align-items: center;
      justify-content: center;
    }
    .ez-cabinet-menu-widget .ez-action-btn .anticon,
    .ez-cabinet-menu-widget .ez-action-btn svg {
      color: ${sc(menuActionIcon)} !important;
      font-size: ${sc(props.actionIconSize || "18px")} !important;
    }
    .ez-cabinet-menu-widget .ant-menu-inline-collapsed .ant-menu-item,
    .ez-cabinet-menu-widget .ant-menu-inline-collapsed .ant-menu-submenu-title {
      padding: 0 !important;
      text-align: center;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .ez-cabinet-menu-widget .ant-menu-inline-collapsed .ant-menu-item .anticon,
    .ez-cabinet-menu-widget .ant-menu-inline-collapsed .ant-menu-item > span:first-child {
      margin: 0 !important;
      font-size: 16px;
    }
    .ez-cabinet-menu-widget.ant-drawer .ant-drawer-body,
    .ez-cabinet-menu-widget.ant-drawer .ant-drawer-header {
      background: ${sc(menuBg)} !important;
    }
    .ez-cabinet-menu-widget.ant-drawer .ant-drawer-title {
      ${props.textColor ? `color: ${sc(menuText)} !important;` : ""}
      ${props.fontFamily ? `font-family: ${sc(props.fontFamily)} !important;` : ""}
    }
  `;

  const dropdownCss = `
    .ez-cabinet-dropdown .ant-dropdown-menu {
      background-color: ${sc(menuDropdownBg)} !important;
      box-shadow: 0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05) !important;
      border-radius: 8px !important;
    }
    .ez-cabinet-dropdown .ant-dropdown-menu-item {
      color: ${sc(menuDropdownText)} !important;
      font-size: ${sc(props.dropdownFontSize || "14px")} !important;
    }
    .ez-cabinet-dropdown .ant-dropdown-menu-item .anticon {
      color: ${sc(menuDropdownIcon)} !important;
    }
    .ez-cabinet-dropdown .ant-dropdown-menu-item:hover,
    .ez-cabinet-dropdown .ant-dropdown-menu-submenu-title:hover {
      background-color: ${sc(token.colorFillSecondary)} !important;
    }
    .ez-cabinet-dropdown .ant-dropdown-menu-submenu-title {
      color: ${sc(menuDropdownText)} !important;
      font-size: ${sc(props.dropdownFontSize || "14px")} !important;
    }
    .ez-cabinet-dropdown .ant-dropdown-menu-submenu-title .anticon {
      color: ${sc(menuDropdownIcon)} !important;
    }
    .ez-cabinet-dropdown .ant-dropdown-menu-submenu .ant-dropdown-menu {
      background-color: ${sc(menuDropdownBg)} !important;
    }
    .ez-cabinet-dropdown .ant-dropdown-menu-submenu .ant-dropdown-menu-item {
      color: ${sc(menuDropdownText)} !important;
      font-size: ${sc(props.dropdownFontSize || "14px")} !important;
    }
    .ez-cabinet-dropdown .ant-dropdown-menu-submenu .ant-dropdown-menu-item:hover {
      background-color: ${sc(token.colorFillSecondary)} !important;
    }
  `;

  const accountMenuItems: MenuProps["items"] = useMemo(
    () => [
      {
        key: "profile",
        onClick: () => setIsProfileDrawerVisible(true),
        label: loc.word("Profile"),
        icon: <UserOutlined />,
      },
      {
        key: "lang",
        type: "submenu" as const,
        icon: <GlobalOutlined />,
        label: loc.word(`language.${lang}`),
        popupClassName: "ez-cabinet-dropdown",
        children: loc.langs
          .filter((x: string) => x !== lang)
          .map((x: string) => ({
            key: x,
            onClick: () => selectLang(x as Lang),
            label: loc.word(`language.${x}`),
          })),
      },
      {
        key: "bonus",
        onClick: () => setIsProfileDrawerVisible(true, "bonus"),
        icon: <DollarOutlined />,
        label: (
          <>
            <Loc word="Contact.bonusBalance" />
            &nbsp;
            {user?.bonusBalance && (
              <Price price={user?.bonusBalance} className="color-success" />
            )}
          </>
        ),
      },
      {
        key: "qrcode",
        onClick: () => setIsProfileDrawerVisible(true, "qrcode"),
        icon: <QrcodeOutlined />,
        label: <Loc word="QRCode.title" default="QR Code for login" />,
      },
      { type: "divider" as const },
      {
        key: "logout",
        onClick: () => logout(),
        icon: <LogoutOutlined />,
        label: loc.word("Logout", { default: "Logout" }),
      },
    ],
    [
      lang,
      logout,
      selectLang,
      setIsProfileDrawerVisible,
      user?.bonusBalance,
      loc,
    ],
  );

  const renderActions = (vertical?: boolean) => (
    <div
      style={{
        display: "flex",
        flexDirection: vertical ? "column" : "row",
        alignItems: vertical ? "stretch" : "center",
        gap: vertical ? 4 : 8,
      }}
    >
      {props.showGamification && school.gamification && (
        <Button
          type="text"
          className="ez-gam-badge"
          style={{
            backgroundColor: props.gamBgColor || token.colorFillTertiary,
            color: props.gamTextColor || "#1890ff",
            fontSize: props.gamFontSize || "14px",
            fontWeight: 600,
            padding: `${props.gamPaddingV || "4px"} ${props.gamPaddingH || "8px"}`,
            borderRadius: props.gamBorderRadius || "4px",
            height: "auto",
            display: "flex",
            alignItems: "center",
            gap: 4,
            border: "none",
          }}
          onClick={() => navigate("/gamification")}
        >
          <GamificationBalance />
        </Button>
      )}

      {props.showChat && school.supportChatEnabled && device.chatController && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            cursor: "pointer",
            ...(vertical ? { padding: "6px 0" } : {}),
          }}
          onClick={() => {
            if (device.chatController?.visible) device.chatController.close();
            else device.chatController?.open();
          }}
        >
          <Badge count={device.chatUnreadCount}>
            <Button
              icon={device.isChatInited ? <BsChatDots /> : <LoadingOutlined />}
              type="text"
              shape="circle"
              className="ez-action-btn"
            />
          </Badge>
          {vertical && !collapsed && (
            <span
              style={{
                fontSize: props.accountNameSize || "12px",
                color: menuText,
              }}
            >
              {loc.word("Chat", { default: "Chat" })}
            </span>
          )}
        </div>
      )}

      {props.showNotifications && (
        <>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              cursor: "pointer",
              ...(vertical ? { padding: "6px 0" } : {}),
            }}
            onClick={() => {
              if (notificationDrawer.visible) notificationDrawer.close();
              else notificationDrawer.open();
            }}
          >
            <Badge count={unreadCount} overflowCount={99}>
              <Button
                icon={<BellOutlined />}
                type="text"
                shape="circle"
                className="ez-action-btn"
              />
            </Badge>
            {vertical && !collapsed && (
              <span
                style={{
                  fontSize: props.accountNameSize || "12px",
                  color: menuText,
                }}
              >
                {loc.word("Notifications", { default: "Notifications" })}
              </span>
            )}
          </div>
        </>
      )}

      {props.showAccount && user && (
        <Dropdown
          menu={{ items: accountMenuItems }}
          trigger={["click"]}
          placement={vertical ? "rightBottom" : "bottomRight"}
          overlayStyle={{
            minWidth: 200,
            ...(vertical ? { marginLeft: 8 } : { marginTop: 8 }),
          }}
          overlayClassName="ez-cabinet-dropdown"
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              cursor: "pointer",
              ...(vertical ? { padding: "6px 0" } : {}),
            }}
            onClick={(e) => e.preventDefault()}
          >
            <UserAvatar
              user={user}
              size={parseInt(props.accountAvatarSize || "32") || 32}
            />
            {!collapsed && props.accountShowName !== false && (
              <>
                <div
                  className="ez-account-name"
                  style={{
                    fontSize: props.accountNameSize || "12px",
                    color: props.accountNameColor || menuText,
                    flex: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {user.fullName}
                </div>
                <DownOutlined
                  className="ez-account-caret"
                  style={{
                    fontSize: 10,
                    color: props.accountNameColor || menuText,
                  }}
                />
              </>
            )}
          </div>
        </Dropdown>
      )}
    </div>
  );

  const notificationDrawerEl = props.showNotifications && (
    <Drawer
      open={notificationDrawer.visible}
      onClose={() => notificationDrawer.close()}
      closeIcon={null}
      title={null}
      width={device.deviceSize === "mobile" ? "100%" : 500}
      destroyOnHidden
      className="p-0 m-0 no-header"
    >
      <Suspense fallback={<LoadingOverlay />}>
        <UserNotifications
          onSettings={() => {
            setIsProfileDrawerVisible(true, "notifications");
            notificationDrawer.close();
          }}
          onClose={() => notificationDrawer.close()}
        />
      </Suspense>
    </Drawer>
  );

  if (isMobile && !isVertical) {
    return (
      <div
        className="ez-cabinet-menu-widget ez-cabinet-menu-mobile"
        style={{
          backgroundColor: menuBg,
          maxWidth: "100%",
          boxSizing: "border-box",
          ...(props.sticky
            ? { position: "sticky", top: 0, zIndex: "var(--z-sticky, 100)", flexShrink: 0 }
            : { flexShrink: 0 }),
        }}
      >
        <style>
          {scopedCss}
          {dropdownCss}
          {`
            .ez-cabinet-menu-mobile .ez-account-name { display: none !important; }
            .ez-cabinet-menu-mobile .ez-account-caret { display: none !important; }
            .ez-cabinet-menu-mobile .ez-gam-badge { display: none !important; }
          `}
        </style>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            height: 56,
            padding: "0 8px",
            gap: 4,
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flex: "1 1 auto",
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            <Button
              type="text"
              icon={<MenuOutlined style={{ fontSize: props.burgerIconSize || undefined }} />}
              onClick={() => setMobileOpen(true)}
              style={{
                color: props.burgerIconColor || menuText,
                fontSize: props.burgerIconSize || undefined,
                flexShrink: 0,
              }}
            />
            {props.showLogo && (
              <div
                className="ez-cabinet-menu-mobile-logo"
                style={{
                  cursor: "pointer",
                  flex: "0 1 auto",
                  minWidth: 0,
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                }}
                onClick={() => navigate("/")}
              >
                <SchoolLogo height={36} />
              </div>
            )}
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
              gap: 4,
            }}
          >
            {renderActions()}
          </div>
        </div>
        <Drawer
          placement={props.mobileMenuPlacement || "left"}
          mask={props.mobileMenuMask !== false}
          rootClassName="ez-cabinet-menu-drawer"
          classNames={{ content: "ez-cabinet-menu-widget" }}
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          width={Math.min(
            parseInt(props.burgerWidth || "280") || 280,
            typeof window !== "undefined" ? window.innerWidth - 48 : 280,
          )}
          title={loc.word("Menu", { default: "Menu" })}
        >
          <style>{scopedCss}</style>
          <AntMenu
            mode="inline"
            selectedKeys={[window.location.pathname]}
            items={menuItems}
          />
        </Drawer>
        {notificationDrawerEl}
      </div>
    );
  }

  if (isVertical) {
    const collapsedIconSize = parseInt(props.actionIconSize || "28") || 28;
    const collapsedMenu = collapsed && (
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          gap: 4,
          padding: isMobile ? "8px 4px" : "8px 6px",
        }}
      >
        {(rootPages.length > 0 ? rootPages : []).map((page, i) => {
          const path = i === 0 ? "/" : `/${page.segment}`;
          const isActive = window.location.pathname === path;
          return (
            <div
              key={page.id}
              onClick={() => navigate(path)}
              title={page.name}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: isMobile ? "10px 2px" : "8px 4px",
                gap: 4,
                borderRadius: props.itemBorderRadius || "8px",
                backgroundColor: isActive
                  ? props.itemBgActiveColor || "#e6f4ff"
                  : props.itemBgColor || "transparent",
                color: isActive
                  ? menuActive
                  : menuText,
                cursor: "pointer",
                transition: "all 0.2s",
              }}
            >
              <div
                style={{
                  lineHeight: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {pageIcon(page, collapsedIconSize)}
              </div>
            </div>
          );
        })}
      </div>
    );

    const actionIconSizePx = parseInt(props.actionIconSize || "18") || 18;
    const actionBtnSizePx = actionIconSizePx + 14;
    const actionIconColor = props.actionIconColor || "#666";
    const actionCellStyle: React.CSSProperties = {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "6px 4px",
      gap: 4,
      cursor: "pointer",
      width: "100%",
    };
    const actionIconWrapStyle: React.CSSProperties = {
      width: actionBtnSizePx,
      height: actionBtnSizePx,
      borderRadius: props.actionBorderRadius || "50%",
      backgroundColor: menuActionBg,
      color: actionIconColor,
      fontSize: actionIconSizePx,
      lineHeight: 1,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    };
    const collapsedActions = collapsed && (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          gap: 4,
          padding: isMobile ? "8px 4px" : "8px 6px",
          borderTop: "1px solid #f0f0f0",
        }}
      >
        {props.showGamification && school.gamification && (
          <div
            style={{ ...actionCellStyle, padding: "6px 4px" }}
            onClick={() => navigate("/gamification")}
            title={loc.word("Gamification", { default: "Gamification" })}
          >
            <GamificationBalance vertical iconSize={actionIconSizePx} />
          </div>
        )}
        {props.showChat &&
          school.supportChatEnabled &&
          device.chatController && (
            <div
              style={actionCellStyle}
              title={loc.word("Chat", { default: "Chat" })}
              onClick={() =>
                device.chatController?.visible
                  ? device.chatController.close()
                  : device.chatController?.open()
              }
            >
              <div style={actionIconWrapStyle}>
                {device.isChatInited ? <BsChatDots /> : <LoadingOutlined />}
              </div>
            </div>
          )}
        {props.showNotifications && (
          <div
            style={actionCellStyle}
            title={loc.word("Notifications", { default: "Notifications" })}
            onClick={() =>
              notificationDrawer.visible
                ? notificationDrawer.close()
                : notificationDrawer.open()
            }
          >
            <Badge count={unreadCount} overflowCount={99} size="small">
              <div style={actionIconWrapStyle}>
                <BellOutlined style={{ color: "inherit" }} />
              </div>
            </Badge>
          </div>
        )}
        {props.showAccount && user && (
          <Dropdown
            menu={{ items: accountMenuItems }}
            trigger={["click"]}
            placement="rightBottom"
            overlayClassName="ez-cabinet-dropdown"
            overlayStyle={{ minWidth: 200, marginLeft: 8 }}
          >
            <div style={actionCellStyle} title={user.fullName}>
              <UserAvatar user={user} size={actionBtnSizePx} />
            </div>
          </Dropdown>
        )}
      </div>
    );

    const expandedWidth = isMobile ? 200 : 240;
    const collapsedWidth = isMobile ? 56 : 64;
    const logoPaddingExpanded = isMobile ? "12px 12px" : "12px 16px";
    const logoPaddingCollapsed = isMobile ? "8px 4px" : "12px 8px";
    const logoMiniSize = isMobile ? 28 : 32;

    return (
      <div
        className="ez-cabinet-menu-widget"
        style={{
          backgroundColor: menuBg,
          borderRight: "1px solid #f0f0f0",
          width: collapsed ? collapsedWidth : expandedWidth,
          minHeight: "100%",
          display: "flex",
          flexDirection: "column",
          transition: "width 0.2s",
          overflowX: "visible",
          overflowY: "auto",
          ...(props.sticky
            ? { position: "sticky", top: 0, zIndex: "var(--z-sticky, 100)", flexShrink: 0 }
            : { flexShrink: 0 }),
        }}
      >
        <style>
          {scopedCss}
          {dropdownCss}
        </style>
        {props.showLogo && (
          <div
            style={{
              padding: collapsed ? logoPaddingCollapsed : logoPaddingExpanded,
              cursor: "pointer",
              textAlign: collapsed ? "center" : undefined,
            }}
            onClick={() => navigate("/")}
          >
            <SchoolLogo height={collapsed ? logoMiniSize : props.logoHeight} />
          </div>
        )}
        {collapsed ? (
          collapsedMenu
        ) : (
          <AntMenu
            mode="inline"
            selectedKeys={[window.location.pathname]}
            items={menuItems}
            style={{ flex: 1 }}
          />
        )}
        {collapsed ? (
          collapsedActions
        ) : (
          <div style={{ padding: "8px 16px", borderTop: "1px solid #f0f0f0" }}>
            {renderActions(true)}
          </div>
        )}
        {notificationDrawerEl}
        {isVertical && (
          <div
            style={{
              padding: "8px",
              borderTop: "1px solid #f0f0f0",
              textAlign: "center",
            }}
          >
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{ color: menuText }}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className="ez-cabinet-menu-widget"
      style={{
        backgroundColor: menuBg,
        borderBottom: "1px solid #f0f0f0",
        overflow: "hidden",
        ...(props.sticky
          ? { position: "sticky", top: 0, zIndex: "var(--z-sticky, 100)", flexShrink: 0 }
          : { flexShrink: 0 }),
      }}
    >
      <style>
        {scopedCss}
        {dropdownCss}
      </style>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          height: 64,
          padding: "0 24px",
          gap: 20,
          overflow: "hidden",
        }}
      >
        {props.showLogo && (
          <div
            style={{ cursor: "pointer", flexShrink: 0 }}
            onClick={() => navigate("/")}
          >
            <SchoolLogo height={props.logoHeight} />
          </div>
        )}
        <AntMenu
          mode="horizontal"
          selectedKeys={[window.location.pathname]}
          items={menuItems}
          style={{ flex: 1, minWidth: 0, lineHeight: "62px" }}
        />
        {renderActions()}
      </div>
      {notificationDrawerEl}
    </div>
  );
};

export const parseCabinetMenuProps = (
  container: HTMLElement,
): CabinetMenuWidgetProps => {
  const a = (name: string, fallback = "") => attr(container, name, fallback);
  const b = (name: string, fallback = false) => bool(container, name, fallback);
  return {
    showLogo: b("data-show-logo", true),
    logoHeight: parseInt(a("data-logo-height", "40")) || 40,
    orientation: a("data-orientation", "horizontal") as
      | "horizontal"
      | "vertical",
    bgColor: a("data-bg-color", "#ffffff"),
    textColor: a("data-text-color", "#333333"),
    activeColor: a("data-active-color", "#1890ff"),
    showActiveBar: b("data-show-active-bar", true),
    mobileMenuPlacement: a("data-mobile-menu-placement", "left") as "left" | "right",
    mobileMenuMask: b("data-mobile-menu-mask", true),
    burgerIconSize: a("data-burger-icon-size", "20px"),
    burgerIconColor: a("data-burger-icon-color"),
    burgerWidth: a("data-burger-width", "280px"),
    fontFamily: a("data-font-family"),
    fontSize: a("data-font-size", "14px"),
    sticky: b("data-sticky", true),
    showChat: b("data-show-chat", true),
    showNotifications: b("data-show-notifications", true),
    showGamification: b("data-show-gamification", true),
    showAccount: b("data-show-account", true),
    itemBgColor: a("data-item-bg-color"),
    itemBgActiveColor: a("data-item-bg-active-color"),
    itemPaddingV: a("data-item-padding-v", "8px"),
    itemPaddingH: a("data-item-padding-h", "16px"),
    itemBorderRadius: a("data-item-border-radius", "0px"),
    itemShadow: a("data-item-shadow"),
    itemGap: a("data-item-gap", "4px"),
    actionIconColor: a("data-action-icon-color", "#666666"),
    actionBgColor: a("data-action-bg-color", "#f5f5f5"),
    actionIconSize: a("data-action-icon-size", "18px"),
    actionBorderRadius: a("data-action-border-radius", "50%"),
    accountNameColor: a("data-account-name-color"),
    accountNameSize: a("data-account-name-size", "12px"),
    accountAvatarSize: a("data-account-avatar-size", "32px"),
    accountShowName: b("data-account-show-name", true),
    dropdownBgColor: a("data-dropdown-bg-color", "#ffffff"),
    dropdownTextColor: a("data-dropdown-text-color", "#333333"),
    dropdownIconColor: a("data-dropdown-icon-color", "#666666"),
    dropdownFontSize: a("data-dropdown-font-size", "14px"),
    gamBgColor: a("data-gam-bg-color", "#e6f7ff"),
    gamTextColor: a("data-gam-text-color", "#1890ff"),
    gamFontSize: a("data-gam-font-size", "14px"),
    gamPaddingV: a("data-gam-padding-v", "4px"),
    gamPaddingH: a("data-gam-padding-h", "8px"),
    gamBorderRadius: a("data-gam-border-radius", "4px"),
    profileBgColor: a("data-profile-bg-color"),
    profileFontFamily: a("data-profile-font-family"),
    profileNameColor: a("data-profile-name-color"),
    profileNameFontSize: a("data-profile-name-font-size"),
    profileEmailColor: a("data-profile-email-color"),
    profileEmailFontSize: a("data-profile-email-font-size"),
    profileBtnBgColor: a("data-profile-btn-bg-color"),
    profileBtnTextColor: a("data-profile-btn-text-color"),
    profileBtnBorderRadius: a("data-profile-btn-border-radius"),
  };
};
